import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiUser, FiMail, FiCheckCircle, FiMessageCircle } from "react-icons/fi";
import { HiOutlineDocumentReport } from "react-icons/hi";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import config from "../config/config.json";
import axios from "axios";
import "../styles/Contact.css";
import { GoogleGenAI } from "@google/genai";
import { Link } from "react-router-dom";
import Header from "../components/Header";

const ai = new GoogleGenAI({
  apiKey: "AIzaSyB27BJqtApZU3qU7UgIQlc9HGEQlen5vPA",
});

async function extractFirstAndLastName(text) {
  const response = await ai.models.generateContent({
    model: "gemini-2.0-flash",
    contents: `Extract first name of the user from this sentence: ${text}. Give only names in response.`,
    config: {
      systemInstruction:
        "You are an intelligent Ai Assistant. You can extract first, last, middle(optional) name of the user from any sentence.",
    },
  });
  return response.text;
}

const AIContactForm = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processingText, setProcessingText] = useState("Processing");

  const steps = [
    {
      key: "name",
      question: (
        <>
          <p>
            <b>Hello! I’m BETSY, UTahData’s AI Assistant.</b> I’d be happy to
            help you with a data request or feedback.{" "}
          </p>
          <p className="mt-1">Could you please share your full name?</p>
        </>
      ),
      icon: <FiUser />,
    },
    {
      key: "email",
      question: formData.firstname
        ? `Thanks, nice to meet you ${formData.firstname}! What’s your email address?`
        : "Great! What's your email address?",
      icon: <FiMail />,
    },
    {
      key: "reportType",
      question: "Please describe in detail what I can help you with.",
      icon: <HiOutlineDocumentReport />,
    },
    {
      key: "comments",
      question:
        "Any additional comments? If you'd like to expedite the request, please share your contact number.",
      icon: <FiMessageCircle />,
    },
  ];

  const validateInput = (key, value) => {
    switch (key) {
      case "name":
        return value.trim().length >= 3
          ? ""
          : "Name must be at least 3 characters.";
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
          ? ""
          : "Invalid email format.";
      case "reportType":
        return value.trim().length >= 3
          ? ""
          : "Please enter a valid report type.";
      case "comments":
        return value.trim().length === 0 || value.trim().length >= 5
          ? ""
          : "Comment must be at least 5 characters.";
      default:
        return "";
    }
  };

  const handleNext = async () => {
    const currentStep = steps[stepIndex];
    const validationMsg = validateInput(currentStep.key, inputValue);

    if (validationMsg) {
      setError(validationMsg);
      return;
    }

    let updatedData;

    if (currentStep.key === "name") {
      const firstname = await extractFirstAndLastName(inputValue);
      updatedData = {
        ...formData,
        name: inputValue,
        firstname: `${firstname}`,
      };
    } else {
      updatedData = {
        ...formData,
        [currentStep.key]: inputValue,
      };
    }

    setFormData(updatedData);
    setInputValue("");
    setError("");

    if (stepIndex + 1 < steps.length) {
      setStepIndex(stepIndex + 1);
    } else {
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${config.APP_URL}/technology/contact`,
          updatedData
        );
        if (response.status === 200) {
          setIsSubmitted(true);
        } else {
          alert(response.data.message || "Submission failed.");
        }
      } catch (error) {
        console.error("API error:", error);
        alert(
          error.response?.data?.message ||
            "An error occurred while submitting the form."
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleReset = () => {
    setFormData({});
    setStepIndex(0);
    setInputValue("");
    setError("");
    setIsSubmitted(false);
  };

  useEffect(() => {
    let interval;
    if (isLoading) {
      let dotCount = 0;
      interval = setInterval(() => {
        dotCount = (dotCount + 1) % 4;
        setProcessingText("Processing" + ".".repeat(dotCount));
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  const dynamicSteps = [
    ...steps,
    ...(isLoading
      ? [
          {
            key: "processing",
            question: <p className="italic text-gray-500">{processingText}</p>,
            icon: <FiCheckCircle />,
          },
        ]
      : []),
    ...(isSubmitted
      ? [
          {
            key: "thankYou",
            question: (
              <p className="text-xl font-semibold">
                <FiCheckCircle className="inline mr-2 text-green-600" />
                Thank you! Your request has been submitted successfully. Our
                team will reach out to you soon.
              </p>
            ),
            icon: <FiCheckCircle />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Header />
      <section
        className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat py-12"
        style={{
          backgroundImage: "url('/img/contact_us.jpeg')",
        }}
      >
        <h2 className="mt-10">Request Data Report</h2>

        <div className="contact-section bg-white/55 mx-[14rem] mt-10 backdrop-blur-md p-6 rounded-2xl shadow-[0_4px_20px_rgba(1,76,178,0.25)]">
          <div className="contact-intro">
            <div className="contact-intro-sec1">
              <h2 className="text-[16px] font-bold text-gray-800 text-center mt-6 italic">
                Submit a Data Request or Feedback to Betsy
              </h2>
            </div>
            <div className="contact-intro-sec2">
              <p>
                We're here to help you explore Utah’s data landscape. Use this
                form to request specific reports, provide feedback, or ask for
                custom insights to support your research, planning, or
                decision-making.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-b from-blue-50 to-white py-12 px-4">
          <div className="max-w-[80%] mx-auto bg-white/25 backdrop-blur-md p-6 rounded-2xl shadow-[0_4px_20px_rgba(1,76,178,0.25)] text-[16px]">
            <div className="space-y-6">
              {dynamicSteps.map((step, index) => (
                <div key={step.key}>
                  {(index <= stepIndex ||
                    formData[step.key] ||
                    step.key === "processing" ||
                    step.key === "thankYou") && (
                    <>
                      <motion.div
                        className="flex items-start gap-2"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                      >
                        <img
                          src="/img/kcgbetsy/Betsy.png"
                          alt="Betsy AI"
                          className="w-24 h-24 rounded-full border border-blue-200 object-cover"
                        />
                        <div className="bg-gray-100 px-4 py-2 rounded-lg max-w-full leading-relaxed text-gray-800 font-sans text-[20px]">
                          {step.question}
                        </div>
                      </motion.div>

                      {formData[step.key] && (
                        <motion.div
                          className="flex justify-end mt-2"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                        >
                          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg max-w-md text-[20px]">
                            <p>{formData[step.key]}</p>
                          </div>
                        </motion.div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {!isSubmitted && !isLoading ? (
              <div className="mt-6">
                <input
                  type="text"
                  placeholder="Type your response..."
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-blue-600 shadow-sm text-[16px]"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && inputValue.trim() !== "") {
                      handleNext();
                    }
                  }}
                />
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                <button
                  onClick={handleNext}
                  disabled={!inputValue.trim() || isLoading}
                  className="mt-3 bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition float-right flex items-center gap-2 text-[16px]"
                >
                  {isLoading ? (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                  ) : (
                    "Send"
                  )}
                </button>
              </div>
            ):""}

            {isSubmitted && (
              <motion.div
                className="mt-10 text-center text-green-700 text-[16px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <button
                  onClick={handleReset}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                >
                  Clear Chat & Submit Another Request
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AIContactForm;
